import { useEffect, useState } from "react";
import { masterApi } from "@/contexts/MasterAuthContext";
import { toast } from "sonner";
import { Inbox, Phone, Mail, Trash2, Filter, Crown, Building2, User as UserIcon, RefreshCw } from "lucide-react";

const STATUS_OPTIONS = [
    { key: "new", label: "Yeni", className: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30" },
    { key: "contacted", label: "Aranıldı", className: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
    { key: "qualified", label: "Nitelikli", className: "bg-purple-500/10 text-purple-400 border-purple-500/30" },
    { key: "converted", label: "Müşteri", className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
    { key: "rejected", label: "Reddedildi", className: "bg-red-500/10 text-red-400 border-red-500/30" },
];

const STATUS_MAP = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s.key]: s }), {});

export default function MasterLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await masterApi.get("/master/leads", { params: filter !== "all" ? { status: filter } : {} });
            setLeads(r.data || []);
        } catch (e) {
            toast.error("Yükleme hatası: " + (e?.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

    const updateStatus = async (id, status) => {
        try {
            await masterApi.patch(`/master/leads/${id}`, { status });
            setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
            toast.success("Durum güncellendi");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message);
        }
    };

    const remove = async (id) => {
        if (!confirm("Bu demo talebi silinsin mi?")) return;
        try {
            await masterApi.delete(`/master/leads/${id}`);
            setLeads((prev) => prev.filter((l) => l.id !== id));
            toast.success("Silindi");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message);
        }
    };

    const counts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s.key]: leads.filter((l) => l.status === s.key).length }), {});

    return (
        <div data-testid="master-leads-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-yellow-400 mb-2 flex items-center gap-2">
                        <Crown size={14}/> Master · Demo Talepleri
                    </div>
                    <h1 className="text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
                        <Inbox className="w-8 h-8 text-yellow-400" /> Leads
                    </h1>
                    <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
                        DR AI FUTBOL tanıtım sitesi (`draifutbol.com/demo-talep`) üzerinden gelen demo talepleri. Aramayı/dönüşü buradan takip edebilirsiniz.
                    </p>
                </div>
                <button
                    onClick={refresh}
                    data-testid="leads-refresh-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] text-sm rounded-lg transition-colors"
                >
                    <RefreshCw size={14} /> Yenile
                </button>
            </div>

            {/* Filter tabs */}
            <div data-testid="leads-filter-tabs" className="flex flex-wrap gap-2 mb-6">
                <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">
                    Tümü ({leads.length})
                </FilterChip>
                {STATUS_OPTIONS.map((s) => (
                    <FilterChip key={s.key} active={filter === s.key} onClick={() => setFilter(s.key)} testId={`filter-${s.key}`}>
                        {s.label} ({counts[s.key] || 0})
                    </FilterChip>
                ))}
            </div>

            {loading ? (
                <div data-testid="leads-loading" className="text-neutral-500 text-sm">Yükleniyor…</div>
            ) : leads.length === 0 ? (
                <div data-testid="leads-empty" className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-12 text-center">
                    <Inbox className="w-12 h-12 text-neutral-700 mx-auto mb-3" />
                    <div className="text-neutral-400">{filter === "all" ? "Henüz demo talebi yok." : "Bu durumda talep yok."}</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {leads.map((lead) => (
                        <LeadCard key={lead.id} lead={lead} onStatusChange={updateStatus} onDelete={remove} />
                    ))}
                </div>
            )}
        </div>
    );
}

function FilterChip({ active, onClick, children, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full border transition ${
                active
                    ? "bg-yellow-500 text-black border-yellow-500"
                    : "bg-[#1a1a1a] text-neutral-400 border-[#2a2a2a] hover:border-neutral-600"
            }`}
        >
            {children}
        </button>
    );
}

function LeadCard({ lead, onStatusChange, onDelete }) {
    const status = STATUS_MAP[lead.status] || STATUS_OPTIONS[0];
    const date = lead.created_at ? new Date(lead.created_at).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }) : "—";
    return (
        <div data-testid={`lead-card-${lead.id}`} className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-5 hover:border-[#3a3a3a] transition-colors">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-xl font-extrabold tracking-tight">{lead.ad_soyad}</h3>
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${status.className}`} data-testid={`lead-status-${lead.id}`}>
                            {status.label}
                        </span>
                    </div>
                    <div className="text-sm text-neutral-400 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5">
                            <Building2 size={13} className="text-yellow-400/70" /> <strong className="text-neutral-200">{lead.kulup_adi}</strong>
                        </span>
                        {lead.rol && (
                            <>
                                <span className="text-neutral-700">·</span>
                                <span className="inline-flex items-center gap-1.5">
                                    <UserIcon size={13} className="text-yellow-400/70" /> {lead.rol}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <select
                        value={lead.status}
                        onChange={(e) => onStatusChange(lead.id, e.target.value)}
                        data-testid={`lead-status-select-${lead.id}`}
                        className="bg-[#0a0a0a] border border-[#2a2a2a] text-sm px-3 py-2 rounded-lg outline-none focus:border-yellow-500"
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => onDelete(lead.id)}
                        data-testid={`lead-delete-${lead.id}`}
                        className="p-2 text-neutral-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Sil"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-sm">
                <a href={`tel:${lead.telefon}`} className="inline-flex items-center gap-2 text-neutral-300 hover:text-yellow-400 transition" data-testid={`lead-phone-${lead.id}`}>
                    <Phone size={14} className="text-yellow-400" /> {lead.telefon}
                </a>
                {lead.email && (
                    <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-2 text-neutral-300 hover:text-yellow-400 transition" data-testid={`lead-email-${lead.id}`}>
                        <Mail size={14} className="text-yellow-400" /> {lead.email}
                    </a>
                )}
                <div className="inline-flex items-center gap-2 text-neutral-500">
                    <Filter size={14} className="text-yellow-400/60" /> kaynak: <code className="text-yellow-400/80 text-xs">{lead.kaynak || "—"}</code>
                </div>
            </div>

            {lead.mesaj && (
                <div className="mt-3 p-3 bg-[#0a0a0a] border border-[#1f1f1f] rounded-lg text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap">
                    {lead.mesaj}
                </div>
            )}

            <div className="mt-3 text-[10px] uppercase tracking-widest text-neutral-600">{date}</div>
        </div>
    );
}
